// src/pages/Disclaimer.js
import React from "react";
import Seo from "../components/seo";

/* =========================================================
   THEME TOKENS (identical to About.js)
========================================================= */
const WRAP = "px-[1vw]";                // global side padding
const MAXW = "max-w-10xl mx-auto";      // page max width
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " + BORDER_SOFT + " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background (identical to About.js) */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Panels (non-glass, textile-like) — shared look */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO helpers (page-specific)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/disclaimer`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`; // 1200x630 recommended

const metaDescription =
  "TarotFate readings are for reflection and emotional insight only, not medical, legal, or financial advice. Understand the intended use and your responsibilities.";

// Page-specific keywords (your Seo component will append global mega keywords automatically)
const keywords = `
tarot disclaimer, tarot terms, responsible tarot use, not medical advice, not legal advice, not financial advice,
tarot guidance, reflective tarot, emotional insight, website disclaimer, TarotFate disclaimer
`.replace(/\s+/g, " ").trim();

// JSON-LD graph
const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/icons/logo.webp`,
        width: 512,
        height: 512
      }
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: "en"
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Disclaimer | TarotFate",
      description: metaDescription,
      isPartOf: { "@id": `${siteUrl}#website` },
      primaryImageOfPage: { "@id": `${pageUrl}#image` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
      inLanguage: "en"
    },
    {
      "@type": "ImageObject",
      "@id": `${pageUrl}#image`,
      url: ogImage,
      width: 1200,
      height: 630,
      caption: "TarotFate cover"
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Disclaimer", item: pageUrl }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Are TarotFate readings professional advice?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No. Readings are for reflection and emotional/spiritual insight only. They are not medical, psychiatric, psychological, legal, financial, or professional advice."
          }
        },
        {
          "@type": "Question",
          name: "What should I do if I have a medical or legal issue?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Consult a licensed professional in the appropriate field. TarotFate is not a substitute for diagnosis, treatment, or professional consultation."
          }
        },
        {
          "@type": "Question",
          name: "Who is responsible for decisions made after a reading?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "You are. All interpretations and actions are your responsibility. No guarantees are made regarding outcomes or accuracy."
          }
        },
        {
          "@type": "Question",
          name: "Is there an age recommendation?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "TarotFate is intended for mature audiences capable of personal decision-making. Where required by local laws, users must comply with age restrictions."
          }
        }
      ]
    }
  ]
};

export default function Disclaimer() {
  return (
    <div
      className={`${FONT} text-white min-h-screen relative overflow-hidden`}
      style={{ background: CANVAS_BG }}
    >
      <Seo
        title="Disclaimer | TarotFate"
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={schemaGraph}
        keywords={keywords}
      />

      {/* Ambient auras (match About.js) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* =========================
          HERO (same header style)
      ========================= */}
      <section className={`pt-6 ${WRAP} ${MAXW} text-center`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">
          Disclaimer
        </h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100 drop-shadow-[0_0_0px_rgba(203,183,234,0.8)]">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>
          Readings are for reflection and emotional insight, not professional advice.
        </p>
      </section>

      {/* =========================
          DISCLAIMER PANEL (silk, non-glass)
      ========================= */}
      <section className={`mt-8 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-2 sm:p-6 md:p-6`}>
          <div className={`relative z-[1] ${TEXT} text-[17px] md:text-[18px] leading-[1.8]`}>
            <p>
              The information and tarot readings provided on this website are intended solely for
              personal reflection, emotional insight, and general inspiration. They do not constitute
              and should not be interpreted as medical, psychiatric, psychological, legal, financial,
              or professional advice of any kind.
            </p>
            <p className="mt-5">
              While tarot can be a meaningful tool for introspection and emotional support, it is not
              a substitute for professional treatment, diagnosis, or consultation. If you are facing a
              medical condition, mental health concern, legal situation, or financial issue, please
              consult a licensed professional in the appropriate field.
            </p>
            <p className="mt-5">
              All users are responsible for their own interpretations, decisions, and actions based on
              the readings or content provided on this site. No guarantees are made regarding the
              accuracy, completeness, or outcomes of any reading or information shared.
            </p>
            <p className="mt-5">
              By using this website, you acknowledge and agree that your participation is entirely
              voluntary and at your own discretion. The creators, contributors, and administrators of
              this site disclaim all liability for any direct, indirect, incidental, or consequential
              outcomes arising from your use of the site or reliance on any content herein.
            </p>
            <p className="mt-5">
              Tarot is a personal and subjective experience; treat it as a gentle companion, not a
              directive force.
            </p>
          </div>
          {/* top edge gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* =========================
          FAQ — compact, left text + right “+”
      ========================= */}
      <section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
        <div className="text-center">
          <h2 className="text-base sm:text-lg font-medium">Frequently Asked Questions</h2>
        </div>

        <div className="mt-6 rounded-sm border border-white/35 bg-white/5 divide-y divide-white/25">
          {[
            {
              q: "Are TarotFate readings professional advice?",
              a:
                "No. Readings are for reflection and emotional/spiritual insight only. They are not medical, psychiatric, psychological, legal, financial, or professional advice."
            },
            {
              q: "What should I do if I have a medical or legal issue?",
              a:
                "Consult a licensed professional in the appropriate field. TarotFate is not a substitute for diagnosis, treatment, or professional consultation."
            },
            {
              q: "Who is responsible for decisions made after a reading?",
              a:
                "You are. All interpretations and actions are your responsibility. No guarantees are made regarding outcomes or accuracy."
            },
            {
              q: "Is there an age recommendation?",
              a:
                "TarotFate is intended for mature audiences capable of personal decision-making. Where required by local laws, users must comply with age restrictions."
            }
          ].map(({ q, a }, i) => (
            <details key={i} className="group">
              <summary
                className="
                  cursor-pointer list-none px-4 md:px-6 py-3
                  text-sm sm:text-sm text-white/95 font-medium
                  flex items-start justify-between gap-6
                  after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none
                  group-open:after:content-['–']
                "
              >
                <span className="pr-2">{q}</span>
              </summary>
              <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>
                {a}
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
